import Service from './Service';
import Constants from '../submodules/logictry_config/constants';

class Navigation extends Service {
  pageRef;
  maxPage = 0;
  lastPage = 0;

  /** Same counters as logictry_ionic TopLevelRoutes (window.history.state.page). */
  get canGoBack() {
    return (window.history.state?.page ?? 0) > 1;
  }
  get canGoForward() {
    const page = window.history.state?.page ?? 0;
    return page > 0 && page < this.maxPage;
  }

  syncHistoryPage() {
    const currentState = JSON.parse(JSON.stringify(window.history.state)) || {};
    if (!currentState.page) {
      currentState.page = this.lastPage + 1;
      this.maxPage = currentState.page;
      window.history.replaceState(currentState, document.title);
    }
    this.lastPage = currentState.page;
  }

  // The Links
  get company() { return '/company'; }
  get logicCircle() { return '/circle'; }
  get chapters() { return '/chapters'; }
  get logicCircle100() { return '/circle/founding-100'; }
  get logicCircleManaged() { return '/circle/thought-leaders'; }
  get convenings() { return '/convenings'; }
  get womensLogicForum() { return '/convenings/womens-logic-forum'; }
  get worldLogicDay() { return '/convenings/world-logic-day'; }
  get worldLogicDay2027() { return '/convenings/world-logic-day/2027'; }
  get worldLogicForum() { return '/convenings/world-logic-forum'; }
  get account() { return '/account'; }
  get accountBusiness() { return '/account/business'; }
  get accountContacts() { return '/account/contacts'; }
  get accountSettings() { return '/account/settings'; }
  get unsubscribe() { return '/unsubscribe'; }
  get accountFollowRequests() { return '/account/follow-requests'; }
  get activities() { return '/activities'; }
  get ambassadors() { return '/ambassadors'; }
  get analytics() { return '/admin/analytics'; }
  get observabilityErrors() { return '/admin/errors'; }
  get browse() { return '/browse'; }
  get businesses() { return '/businesses'; }
  get careers() { return '/company/careers'; }
  get careerPosition() { return '/company/careers'; }
  get collections() { return '/collections'; }
  get companySettings() { return '/company/settings'; }
  get companies() { return '/companies'; }
  get contactus() { return '/contact-us'; }
  get databases() { return '/databases'; }
  get databaseCollections() { return '/databasecollections'; }
  get departments() { return '/departments'; }
  get documentation() { return '/documentation'; }
  get events() { return '/events'; }
  get teams() { return '/teams'; }
  get home() { return '/'; }
  get industries() { return '/industries'; }
  get invite() { return '/invite'; }
  get legal() { return '/legal' }
  get local() { return '/local'; }
  get map() { return '/map'; }
  get news() { return '/news'; }
  get content() { return '/content'; }
  get assets() { return '/assets'; }
  get onepager() { return '/one-pager'; }
  get partners() { return '/partners'; }
  get pricing() { return '/pricing'; }
  get sitenav() { return '/sitenav'; }
  get services() { return '/services'; }
  get solutions() { return '/solutions'; }
  get spreadsheets() { return '/spreadsheets'; }
  get submissions() { return '/submissions'; }
  get support() { return '/support'; }
  get team() { return '/company/team'; }
  get versions() { return '/versions'; }
  get videos() { return 'https://logictry.com/collections/674ef192453a6702ff950b0b' }
  get welcome() { return '/welcome' };
  userProfile(user) { return `/@${user.username || user._id}`; }
  get mainSiteOrigin() { return 'https://logictry.com'; }
  mainSiteUserProfile(user) {
    return `${this.mainSiteOrigin}${this.userProfile(user)}`;
  }

  /**
   * Marketing CTA target. Content and collections are platform surfaces —
   * Studio has no `/collections` route at all — so landing CTAs always resolve
   * to logictry.com when the app is not already the main site.
   */
  mainSiteCtaLink(_location) {
    if (!_location || Constants.isLogicWiki) return _location;
    if (!/^\/(content|collections)\//.test(_location)) return _location;
    return `${this.mainSiteOrigin}${_location}`;
  }

  /** Push a marketing CTA, opening logictry.com in a new tab when off the main site. */
  pushCta(_location) {
    const href = this.mainSiteCtaLink(_location);
    if (/^https?:\/\//.test(href)) return this.site(href);
    return this.push(href);
  }

  navigate(url, opt) {
    this.previousUrl = window.location.href.split(window.location.origin)[1] || '/';
    if (Constants.isIphone) {
      if (opt?.replace) this.history.replace(url, opt?.state);
      else this.history.push(url, opt?.state);
    } else {
      this.useNavigate(url, opt);
    }
    this.emitStateUpdate();
  }
  handlePopAndReplace(navigationType) {
    const currentUrl = window.location.href.split(window.location.origin)[1] || '/';
    const prevBack = this.canGoBack;
    const prevForward = this.canGoForward;
    this.syncHistoryPage();
    if (navigationType !== 'PUSH' && this.previousUrl && this.previousUrl !== currentUrl) {
      this.emitStateUpdate(navigationType);
    } else if (prevBack !== this.canGoBack || prevForward !== this.canGoForward) {
      this.emitStateUpdate(navigationType);
    }
    this.previousUrl = currentUrl;
  }
  get currentLocation() {
    return window.location.pathname.split('/');
  }
  get currentSearch() {
    const currentSearch = new URLSearchParams(window.location.search);
    const newSearch = {};
    for (const [key, value] of currentSearch) {
      newSearch[key.toLowerCase()] = value;
    }
    return newSearch;
  }
  getQueryParameter(param, dontRedirect) {
    if (!param) return null;
    const queryParameters = this.currentSearch;
    if (!Object.hasOwnProperty.call(queryParameters, param)) return null;
    const value = queryParameters[param];
    delete queryParameters[param];
    if (!dontRedirect) this.navigate(`${window.location.pathname}?${encodeQueryData(queryParameters)}`, { replace: true });
    return value;
  }
  addOrReplaceQueryParam(param, value) {
    const queryParameters = this.currentSearch;
    if (value) queryParameters[param] = value;
    else delete queryParameters[param];
    const qs = encodeQueryData(queryParameters);
    // Replace (don't push) so one-shot query flags like ?tab=featured don't leave a
    // duplicate history entry — Back would otherwise appear to do nothing.
    this.navigate(
      qs ? `${window.location.pathname}?${qs}` : window.location.pathname,
      { replace: true },
    );
  }
  clearQueryParameters() {
    if (!window.location.search) return;
    this.navigate(`${window.location.pathname}`, { replace: true });
  }

  forward() {
    if (Constants.isIphone) this.history.go(1);
    else this.navigate(1);
  }
  back() {
    if (Constants.isIphone) this.history.go(-1);
    else this.navigate(-1);
  }

  push(_location, _state) {
    if (this.__openNewTab || Constants.isWidget) return this.site(_location);

    if (_location === `${window.location.pathname}${window.location.search}`) {
      if (Constants.isIphone) {
        // Scroll up the native ion page
        document.querySelector('ion-content')?.getScrollElement()?.then((s) => (s.scrollTop = 0));
      }
      this.emitStateUpdate('SCROLL_TOP');
      return;
    }

    this.navigate(_location, { state: _state });
  }
  replace(_location) {
    this.navigate(_location, { replace: true });
  }
  site(_site) {
    if (!_site) return null;
    if (_site.indexOf('mailto:') >= 0) {
      window.location = _site;
      return null;
    }
    const win = window.open(_site, '_blank');
    return win;
  }
  search(_query) {
    let query = '/browse?';
    if (_query) Object.keys(_query).forEach((key) => { query = query.concat(`${key}=${_query[key]}&`); });
    query = query.slice(0, -1);
    this.push(query);
  }
}

const singleton = new Navigation();
export default singleton;

function encodeQueryData(data) {
  let encodedString = '';
  for (let key in data) {
    if (Object.prototype.hasOwnProperty.call(data, key)) {
      if (encodedString.length > 0) {
        encodedString += '&';
      }
      encodedString += encodeURIComponent(key) + '=' + encodeURIComponent(data[key]);
    }
  }
  return encodedString;
}
